import React, { useEffect, useState } from 'react';
import { ProjectsTooltip } from './ProjectsTooltip';
import { mockUsers } from '../utils/mockData';
import { MoreVertical, Eye, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Helper function to format date
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};
// Types for sorting
type SortField = 'name' | 'email' | 'mobile' | 'signupDate' | 'projects';
type SortDirection = 'asc' | 'desc';
export const UsersTable = () => {
  const navigate = useNavigate();
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    userId: string | null;
    position: {
      x: number;
      y: number;
    };
  }>({
    visible: false,
    userId: null,
    position: {
      x: 0,
      y: 0
    }
  });
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  // Sorting states
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [sortedUsers, setSortedUsers] = useState([...mockUsers]);
  // Update sorted users when sort parameters change
  useEffect(() => {
    const sortUsers = () => {
      const sorted = [...mockUsers].sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'email':
            comparison = a.email.localeCompare(b.email);
            break;
          case 'mobile':
            comparison = a.mobile.localeCompare(b.mobile);
            break;
          case 'signupDate':
            comparison = a.signupDate.getTime() - b.signupDate.getTime();
            break;
          case 'projects':
            comparison = a.projects.length - b.projects.length;
            break;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
      setSortedUsers(sorted);
    };
    sortUsers();
  }, [sortField, sortDirection]);
  // Handle header click for sorting
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" data-id="element-67" /> : <ChevronDown className="h-4 w-4 ml-1" data-id="element-68" />;
  };
  const handleProjectsClick = (e: React.MouseEvent<HTMLButtonElement>, userId: string) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipData({
      visible: tooltipData.userId === userId ? !tooltipData.visible : true,
      userId,
      position: {
        x: rect.left + window.scrollX,
        y: rect.bottom + window.scrollY
      }
    });
  };
  const toggleActionMenu = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    setActionMenuOpen(actionMenuOpen === userId ? null : userId);
  };
  const handleRowClick = (userId: string) => {
    navigate(`/users/${userId}`);
  };
  const handleViewUser = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    navigate(`/users/${userId}`);
    setActionMenuOpen(null);
  };
  const handleEditUser = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    // For now, just navigate to the user detail page
    navigate(`/users/${userId}`);
    setActionMenuOpen(null);
  };
  const handleDeleteUser = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    // Just close the menu for now, no actual deletion
    setActionMenuOpen(null);
    alert(`Delete user ${userId} (not implemented)`);
  };
  const closeTooltip = () => {
    setTooltipData(prev => ({
      ...prev,
      visible: false
    }));
  };
  // Close tooltip and action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      closeTooltip();
      setActionMenuOpen(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  return <>
      <div className="w-full overflow-auto rounded-md border" data-id="element-69">
        <table className="w-full text-sm" data-id="element-70">
          <thead className="bg-muted/50" data-id="element-71">
            <tr data-id="element-72">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('name')} data-id="element-73">
                <div className="flex items-center" data-id="element-74">
                  Name
                  {renderSortIndicator('name')}
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('email')} data-id="element-75">
                <div className="flex items-center" data-id="element-76">
                  Email
                  {renderSortIndicator('email')}
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('mobile')} data-id="element-77">
                <div className="flex items-center" data-id="element-78">
                  Mobile
                  {renderSortIndicator('mobile')}
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('signupDate')} data-id="element-79">
                <div className="flex items-center" data-id="element-80">
                  Signup Date
                  {renderSortIndicator('signupDate')}
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('projects')} data-id="element-81">
                <div className="flex items-center" data-id="element-82">
                  Projects
                  {renderSortIndicator('projects')}
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground" data-id="element-83">
                Actions
              </th>
            </tr>
          </thead>
          <tbody data-id="element-84">
            {sortedUsers.map(user => <tr key={user.id} className="border-t transition-colors hover:bg-muted/50 cursor-pointer" onClick={() => handleRowClick(user.id)} data-id="element-85">
                <td className="px-4 py-3 font-medium" data-id="element-86">{user.name}</td>
                <td className="px-4 py-3 text-muted-foreground" data-id="element-87">
                  {user.email}
                </td>
                <td className="px-4 py-3 text-muted-foreground" data-id="element-88">
                  {user.mobile}
                </td>
                <td className="px-4 py-3 text-muted-foreground" data-id="element-89">
                  {formatDate(user.signupDate)}
                </td>
                <td className="px-4 py-3" data-id="element-90">
                  <button onClick={e => handleProjectsClick(e, user.id)} className="text-primary underline decoration-dotted underline-offset-4 hover:text-primary/80" data-id="element-91">
                    {user.projects.length}
                  </button>
                </td>
                <td className="px-4 py-3 relative" data-id="element-92">
                  <button onClick={e => toggleActionMenu(e, user.id)} className="p-1 rounded-full hover:bg-muted transition-colors" data-id="element-93">
                    <MoreVertical className="h-4 w-4" data-id="element-94" />
                  </button>
                  {actionMenuOpen === user.id && <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-background border z-10" data-id="element-95">
                      <div className="py-1" data-id="element-96">
                        <button onClick={e => handleViewUser(e, user.id)} className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-muted/50 transition-colors" data-id="element-97">
                          <Eye className="h-4 w-4 mr-2" data-id="element-98" />
                          View
                        </button>
                        <button onClick={e => handleEditUser(e, user.id)} className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-muted/50 transition-colors" data-id="element-99">
                          <Edit className="h-4 w-4 mr-2" data-id="element-100" />
                          Edit
                        </button>
                        <button onClick={e => handleDeleteUser(e, user.id)} className="flex items-center w-full px-4 py-2 text-sm text-left text-red-500 hover:bg-muted/50 transition-colors" data-id="element-101">
                          <Trash2 className="h-4 w-4 mr-2" data-id="element-102" />
                          Delete
                        </button>
                      </div>
                    </div>}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {tooltipData.visible && tooltipData.userId && <ProjectsTooltip projects={mockUsers.find(user => user.id === tooltipData.userId)?.projects || []} position={tooltipData.position} data-id="element-103" />}
    </>;
};